import { useEffect, useState } from "react";

import { Button, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  LearnerFileType,
  ReportSectionFieldsType,
  useUpdateReportSectionFieldMutation,
} from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { PDFWithSelect } from "@pages/ClinicalReportDetails/Components/AssessmentFiles/PDFWithSelect";
import { useFocusedFieldManager } from "@pages/ClinicalReportDetails/Providers/FocusedFieldProvider";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";

export interface AssessmentReportSearchProps {
  learnerFile: LearnerFileType | null;
}

export const AssessmentReportSearch: React.FC<AssessmentReportSearchProps> = ({ learnerFile }) => {
  const { appendContentToField } = useClinicalReportDetailsData();

  const [highlightedText, setHighlightedText] = useState<string>("");
  const [lastSelectedFieldId, setLastSelectedFieldId] = useState<string>("");

  const updateContent = useUpdateReportSectionFieldMutation({});

  const handleOnSave = async (field: ReportSectionFieldsType) => {
    await updateContent
      .mutateAsync({
        reportSectionId: field?.reportSection?.id ?? "<missing-section-id>",
        reportSectionFieldId: field?.id ?? "<missing-section-field-id>",
        content: field.content,
      })
      .catch((error) => {
        console.error("Error while updating field content : ", error);
        notifyError("Error Updating Field Content");
      });
  };

  const focusManager = useFocusedFieldManager();

  useEffect(() => {
    if (focusManager?.focusedId) {
      setLastSelectedFieldId(focusManager.focusedId);
    }
  }, [focusManager?.focusedId]);

  const pasteHighlightedText = (currentFocusedId?: string | null) => {
    if (!currentFocusedId) {
      notifyError("No field selected to insert text into");
      return;
    }
    const cleanHighlightedText = highlightedText.replace(/[\r\n]+/g, " "); // Replace format chars with spaces
    const changedField = appendContentToField(currentFocusedId, cleanHighlightedText);
    if (changedField) {
      handleOnSave(changedField);
    }
    setHighlightedText(""); // Clear the highlighted text after pasting
  };

  return (
    <div className="p-4 pb-0 border-t border-limestone-200">
      <div className="w-full h-[85vh] min-w-[600px] border border-limestone-200 rounded-sm overflow-y-scroll">
        <PDFWithSelect
          fileUrl={learnerFile?.signedS3LearnerFile || ""}
          setSelectedText={setHighlightedText}
          width={600}
        />
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-4 bg-limestone-50 border-t border-limestone-200 z-50">
        <div className="flex items-center justify-between gap-6 pl-4">
          {highlightedText && <Paragraph className="truncate">{highlightedText}</Paragraph>}
          {!highlightedText && (
            <div className="flex items-center justify-center">
              <Label displayType="normal" size="extra-small" colorType="secondary">
                Select text to insert into focused field
              </Label>
            </div>
          )}
          <Button
            className="ml-auto mr-4"
            text="Insert Selection"
            onClick={() => pasteHighlightedText(lastSelectedFieldId)}
            disabled={!highlightedText || !lastSelectedFieldId}
            appearance="primary"
          />
        </div>
      </div>
    </div>
  );
};
